import { useEffect } from "react";
import { usePersistedState } from "../../hooks/usePersistedState";
import Stepper from "../shared/Stepper";
import Step1Gebaeude from "./Step1Gebaeude";
import Step2Massnahme from "./Step2Massnahme";
import Step3Details from "./Step3Details";
import Step4Ergebnis from "./Step4Ergebnis";
import type { ActiveTool } from "../../App";

export interface FoerderFormData {
  // Step 1
  gebaeudetyp?: "efh" | "mfh" | "wohnung";
  baujahr?: number;
  // Step 2
  massnahmen?: string[];
  // Step 3
  eigentuemer?: boolean;
  haushaltseinkommen?: number;
  investitionskosten?: number;
  postleitzahl?: string;
  isfp_vorhanden?: boolean;
  fossile_heizung_vorhanden?: boolean;
  // Paket
  paket?: "basic" | "premium";
}

const STEPS = [
  { label: "Gebäude" },
  { label: "Maßnahme" },
  { label: "Details" },
  { label: "Ergebnis" },
];

interface Props {
  onToolChange?: (tool: ActiveTool) => void;
}

export default function FoerderRechner({ onToolChange }: Props) {
  const [step, setStep] = usePersistedState("neko-foerder-step", 1);
  const [formData, setFormData] = usePersistedState<FoerderFormData>("neko-foerder-data", {});

  const update = (data: Partial<FoerderFormData>) =>
    setFormData((prev) => ({ ...prev, ...data }));

  const next = () => setStep((s) => Math.min(s + 1, 4));
  const back = () => setStep((s) => Math.max(s - 1, 1));

  // Phase 7 / WEB-04: cookieless tool-open event — fires once on mount.
  // No PII — only tool name and tenant_id from server-injected meta tag.
  useEffect(() => {
    if (typeof window !== "undefined" && typeof window.plausible === "function") {
      const slugMeta = document.querySelector('meta[name="x-tenant-slug"]');
      const tenantId = slugMeta?.getAttribute("content") ?? "unknown";
      window.plausible("tool_opened", {
        props: { tool: "foerder-rechner", tenant_id: tenantId },
      });
    }
  }, []);

  return (
    <div className="animate-fade-in" data-testid="foerder-rechner">
      <h1 className="text-xl font-bold text-neko-white mb-1">Förder-Rechner</h1>
      <p className="text-neko-gray text-sm mb-6">
        Berechnen Sie Ihre staatliche Förderung für Heizung &amp; Sanierung.
      </p>

      <Stepper steps={STEPS} currentStep={step} />

      {step === 1 && (
        <Step1Gebaeude
          data={formData}
          onChange={update}
          onNext={next}
        />
      )}
      {step === 2 && (
        <Step2Massnahme
          data={formData}
          onChange={update}
          onNext={next}
          onBack={back}
        />
      )}
      {step === 3 && (
        <Step3Details
          data={formData}
          onChange={update}
          onNext={next}
          onBack={back}
        />
      )}
      {step === 4 && (
        <Step4Ergebnis
          data={formData}
          onBack={back}
          onToolChange={onToolChange}
          onReset={() => {
            setStep(1);
            setFormData({});
            sessionStorage.removeItem("neko-foerder-step");
            sessionStorage.removeItem("neko-foerder-data");
          }}
        />
      )}
    </div>
  );
}
